import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { AcknowledgeModal, CardDashboard, ConfirmationModal, Flex, Modal, Tabs, Text } from '@percent/lemonade'
import { StateMachineBlockVisualiser } from './components/StateMachineBlockVisualiser/StateMachineBlockVisualiser'
import { StateMachineJsonVisualiser } from './components/StateMachineJsonVisualiser/StateMachineJsonVisualiser'
import { StateMachineEditorProvider } from './context/StateMachineEditorContext'
import type { StateMachineSaveErrorDetails, UseStateMachineEditorReturn } from './hooks/useStateMachineEditor'
import { useStateMachine } from './hooks/useStateMachine'
import { useStateMachineEditor } from './hooks/useStateMachineEditor'
import '@xyflow/react/dist/style.css'
import type { ProgramStateMachineProps, StateMachineData, StateMachineNode } from './ProgramStateMachine.types'

const ADD_STATE_SENTINEL = '__add_state__'

const ERROR_REASONS_LIST_STYLE = { textAlign: 'left', paddingLeft: 20 } as const

type TabType = 'ui' | 'code'

export function ProgramStateMachine({ stateMachineDefinition, metadata }: Readonly<ProgramStateMachineProps>) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<TabType>('code')
  type ConfirmModalState = 'confirm' | 'success' | 'error'
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [confirmModalState, setConfirmModalState] = useState<ConfirmModalState>('confirm')
  const [saveErrorDetails, setSaveErrorDetails] = useState<StateMachineSaveErrorDetails | undefined>()

  const stateMachineData = useMemo<StateMachineData | null>(() => {
    if (typeof stateMachineDefinition === 'string') {
      try {
        return JSON.parse(stateMachineDefinition) as StateMachineData
      } catch {
        return null
      }
    }

    return stateMachineDefinition
  }, [stateMachineDefinition])

  const { id } = useParams<{ id?: string }>()

  const onUpdateSuccess = useCallback(() => {
    setIsConfirmModalOpen(true)
    setConfirmModalState('success')
    setSaveErrorDetails(undefined)
  }, [])

  const onUpdateError = useCallback((details?: StateMachineSaveErrorDetails) => {
    setIsConfirmModalOpen(true)
    setConfirmModalState('error')
    setSaveErrorDetails(details)
  }, [])

  const editorValues = useStateMachineEditor({
    stateMachineData,
    programId: id ?? '',
    metadata,
    onSuccess: onUpdateSuccess,
    onError: onUpdateError
  })

  const { nodes, edges, groupNodes, originalJson, editedJson, setEditedJson, handleUpdate, isUpdating } = editorValues

  const { selectedElement, handleSelectionChange } = useStateMachine({
    stateMachineData
  })

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: StateMachineNode) => {
      handleSelectionChange({ nodes: [node], edges: [] })
    },
    [handleSelectionChange]
  )

  const handlePaneClick = useCallback(() => {
    handleSelectionChange({ nodes: [], edges: [] })
  }, [handleSelectionChange])

  const editorContextValue = useMemo<UseStateMachineEditorReturn>(() => editorValues, [editorValues])

  const handleAddState = useCallback(() => {
    handleSelectionChange({
      nodes: [{ id: ADD_STATE_SENTINEL, data: {} as any, position: { x: 0, y: 0 } } as StateMachineNode],
      edges: []
    })
  }, [handleSelectionChange])

  const showConfirmModal = () => {
    setIsConfirmModalOpen(true)
    setConfirmModalState('confirm')
  }

  const closeModal = () => {
    setIsConfirmModalOpen(false)
    setConfirmModalState('confirm')
    setSaveErrorDetails(undefined)
  }

  const getModalContent = () => {
    if (confirmModalState === 'confirm') {
      return (
        <ConfirmationModal
          title={t('dialog.updateStateMachine.confirm.title')}
          primaryButtonText={t('button.yes')}
          secondaryButtonText={t('button.no')}
          handleSubmit={handleUpdate}
          handleClose={closeModal}
          loading={isUpdating}
          disabled={isUpdating}
        >
          <Text size="small">{t('dialog.updateStateMachine.confirm.message', { programId: id })}</Text>
        </ConfirmationModal>
      )
    }

    if (confirmModalState === 'success') {
      return (
        <AcknowledgeModal
          result="positive"
          title={t('dialog.updateStateMachine.success.title')}
          buttonText={t('button.close')}
          handleClose={closeModal}
        >
          <Text size="small">{t('dialog.updateStateMachine.success.text')}</Text>
        </AcknowledgeModal>
      )
    }

    const reasons = saveErrorDetails?.reasons ?? []
    const fallback = saveErrorDetails?.message ?? t('dialog.updateStateMachine.error.description')

    return (
      <AcknowledgeModal
        result="negative"
        title={t('dialog.updateStateMachine.error.title')}
        buttonText={t('button.close')}
        handleClose={closeModal}
      >
        {reasons.length > 0 ? (
          <ul data-testid="state-machine-error-reasons" style={ERROR_REASONS_LIST_STYLE}>
            {reasons.map((reason, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={idx}>
                <Text size="small">{reason}</Text>
              </li>
            ))}
          </ul>
        ) : (
          <Text size="small">{fallback}</Text>
        )}
      </AcknowledgeModal>
    )
  }

  const confirmModal = isConfirmModalOpen && (
    <Modal open={isConfirmModalOpen} onClose={closeModal}>
      {getModalContent()}
    </Modal>
  )

  const tabItems = useMemo(
    () => [
      { key: 'ui', label: t('stateMachine.tabs.blockFlow') },
      { key: 'code', label: t('stateMachine.tabs.code') }
    ],
    [t]
  )

  const handleChangeTab = useCallback((newValue: string) => {
    setActiveTab(newValue as TabType)
  }, [])

  return (
    <CardDashboard title={t('grants.programDetails.stateMachine.title')} data-testid="program-state-machine-card">
      <Flex direction="column" style={{ width: '100%', height: '100%' }}>
        {confirmModal}

        <Tabs activeTab={activeTab} onChangeTab={handleChangeTab} items={tabItems} />

        {activeTab === 'ui' && (
          <StateMachineEditorProvider value={editorContextValue}>
            <StateMachineBlockVisualiser
              nodes={nodes}
              edges={edges}
              groupNodes={groupNodes}
              onNodeClick={handleNodeClick}
              onPaneClick={handlePaneClick}
              onAddState={handleAddState}
              selectedElement={selectedElement}
            />
          </StateMachineEditorProvider>
        )}
        {activeTab === 'code' && (
          <StateMachineJsonVisualiser
            originalJson={originalJson}
            editedJson={editedJson}
            onJsonChange={setEditedJson}
            onUpdate={showConfirmModal}
            isUpdating={isUpdating}
          />
        )}
      </Flex>
    </CardDashboard>
  )
}
